/**
 * Delivery-option diagnostics.
 *
 * Enable in the browser with any of:
 *   ?delivery-debug=1 in the URL
 *   localStorage.setItem('deliveryDebug', '1')
 *   window.DELIVERY_DEBUG = true
 *
 * Logs why the PickEasy delivery options in the cart drawer are or are not
 * visible. For "DHL only" carts the theme hides the whole options section and
 * leaves Checkout only. Otherwise it can still hide Nationwide when products
 * are not nationwide-eligible.
 */
const DeliveryDebug = {
  get enabled() {
    try {
      return (
        window.DELIVERY_DEBUG === true ||
        new URLSearchParams(window.location.search).has('delivery-debug') ||
        localStorage.getItem('deliveryDebug') === '1'
      );
    } catch (error) {
      return window.DELIVERY_DEBUG === true;
    }
  },

  log(...args) {
    if (this.enabled) console.log('%c[delivery]', 'color:#a18d61;font-weight:bold', ...args);
  },

  group(label) {
    if (this.enabled) console.group(`%c[delivery] ${label}`, 'color:#a18d61;font-weight:bold');
  },

  groupEnd() {
    if (this.enabled) console.groupEnd();
  },

  table(rows) {
    if (this.enabled) console.table(rows);
  },

  // Reports the visibility of each fulfilment-type option and the reason for it.
  reportOptions(reason) {
    if (!this.enabled) return;

    const container = document.getElementById('ft-options-div');
    if (!container) {
      this.log('No #ft-options-div in the DOM. The PickEasy app has not rendered its options (yet).');
      return;
    }

    if (container.getAttribute('data-theme-dhl-only') === 'true') {
      this.log(`Whole delivery options section is hidden because ${reason || 'cart contains a "DHL only" product'}`);
      return;
    }

    const rows = [...container.querySelectorAll('.ft-options-cell')].map((cell) => {
      const label = cell.innerText.replace(/\s+/g, ' ').trim() || '(empty - app still rendering)';
      const inlineDisplay = cell.style.display;
      const computed = getComputedStyle(cell);
      const hiddenByTheme = inlineDisplay === 'none';
      const visible = computed.display !== 'none' && computed.visibility !== 'hidden' && cell.offsetParent !== null;
      const isNationwide = !!cell.querySelector('#standard-shipping-option-div');
      const isClickAndCollect = !!cell.querySelector('#pickup-option-div');
      const controlledBy = isNationwide || isClickAndCollect ? 'theme + app' : 'app only';

      let why;
      if (visible) {
        if (isNationwide) {
          why = 'Shown: theme left it alone (all cart products are nationwide-eligible)';
        } else if (isClickAndCollect) {
          why = 'Shown: theme left it alone (cart has no "DHL only" products)';
        } else {
          why = 'Shown: rendered by the PickEasy app, the theme never touches this option';
        }
      } else if (hiddenByTheme && isNationwide) {
        why = `Hidden by the theme (display:none set in cart-drawer.js) - ${reason || 'cart contains a product without a "DHL only" or "Nationwide" tag'}`;
      } else {
        why = 'Hidden by the PickEasy app or by CSS, not by the theme';
      }

      return { option: label, visible, hiddenByTheme, controlledBy, why };
    });

    this.group(`option visibility (${rows.length} found)`);
    this.table(rows);
    rows.forEach((row) => this.log(`${row.visible ? 'VISIBLE' : 'HIDDEN '} | ${row.option} | ${row.why}`));
    this.groupEnd();
  },
};

class CartDrawer extends HTMLElement {
  // How long to wait for the app to render its delivery options before giving up.
  static STANDARD_SHIPPING_TIMEOUT_MS = 8000;

  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.setDrawerFooterLoading(true);
    DeliveryDebug.log('Drawer opened. Watching for the app to render the delivery options.');
    this.syncStandardShippingOption();
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  // Kept public: cart-click-and-collect.js calls this after updating cart attributes.
  async toggleStandardShippingOptionForDhlOnly() {
    const eligibility = await this.getCartDeliveryEligibility();
    this.applyDeliveryOptionVisibility(eligibility);
    DeliveryDebug.reportOptions(this.lastEligibilityReason);
  }

  /**
   * Decides whether PickEasy delivery options should be visible.
   *
   * The options are rendered by the PickEasy app, so the theme cannot know when they
   * exist. Rather than guessing with a fixed delay, the tag check and the wait
   * for the elements run concurrently, and the result is applied as soon as both
   * are ready. A MutationObserver then keeps the decision applied if the app
   * re-renders its options.
   *
   * If the cart contains any "DHL only" product, the whole delivery options
   * section is hidden and Checkout is left alone.
   */
  async syncStandardShippingOption() {
    const eligibilityPromise = this.getCartDeliveryEligibility();

    try {
      const [optionsContainer, eligibility] = await Promise.all([
        this.waitForDeliveryOption('#ft-options-div', 'delivery options'),
        eligibilityPromise,
      ]);

      if (!optionsContainer) {
        DeliveryDebug.log(
          `The app never rendered #ft-options-div within ${CartDrawer.STANDARD_SHIPPING_TIMEOUT_MS}ms,`,
          'so the theme could not show or hide delivery options.'
        );
        return;
      }

      this.applyDeliveryOptionVisibility(eligibility);
      this.observeDeliveryOptions();
    } finally {
      this.setDrawerFooterLoading(false);
      DeliveryDebug.reportOptions(this.lastEligibilityReason);
    }
  }

  // Resolves with the option element as soon as the app renders it, or null on timeout.
  waitForDeliveryOption(selector, label) {
    const existing = document.querySelector(selector);
    if (existing) {
      DeliveryDebug.log(`The app had already rendered the ${label}, no waiting needed.`);
      return Promise.resolve(existing);
    }

    return new Promise((resolve) => {
      const startedAt = Date.now();
      let settled = false;

      const finish = (element) => {
        if (settled) return;
        settled = true;
        observer.disconnect();
        clearTimeout(timeoutId);
        if (element) DeliveryDebug.log(`The app rendered the ${label} after ${Date.now() - startedAt}ms.`);
        resolve(element);
      };

      const observer = new MutationObserver(() => {
        const element = document.querySelector(selector);
        if (element) finish(element);
      });

      const timeoutId = setTimeout(() => finish(null), CartDrawer.STANDARD_SHIPPING_TIMEOUT_MS);

      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  // Backwards-compatible alias used by older call sites.
  waitForStandardShippingOption() {
    return this.waitForDeliveryOption('#standard-shipping-option-div', 'Nationwide option');
  }

  // Re-applies the decision if the app replaces its options while the drawer is open.
  observeDeliveryOptions() {
    const container = document.getElementById('ft-options-div');
    if (!container || this.standardShippingObserver) return;

    this.standardShippingObserver = new MutationObserver(() => {
      if (!this.lastDeliveryEligibility || this.applyingDeliveryVisibility) return;

      const shouldHideSection = this.lastDeliveryEligibility.hasDhlOnly;
      const sectionHidden = container.getAttribute('data-theme-dhl-only') === 'true';

      if (shouldHideSection === sectionHidden && shouldHideSection) return;

      if (!shouldHideSection) {
        const nationwideCell = document.getElementById('standard-shipping-option-div')?.closest('.ft-options-cell');
        const shouldHideNationwide = !this.lastDeliveryEligibility.allNationwideEligible;
        const nationwideMatches =
          !nationwideCell || (nationwideCell.style.display === 'none') === shouldHideNationwide;
        if (nationwideMatches && !sectionHidden) return;
      }

      DeliveryDebug.log('The app re-rendered its options, re-applying the theme decision.');
      this.applyDeliveryOptionVisibility(this.lastDeliveryEligibility);
    });

    this.standardShippingObserver.observe(container.parentElement || container, { childList: true, subtree: true });
  }

  // Backwards-compatible alias.
  observeStandardShippingOption() {
    this.observeDeliveryOptions();
  }

  async getCartDeliveryEligibility() {
    this.lastEligibilityReason = null;

    const emptyResult = { allNationwideEligible: false, hasDhlOnly: false };

    try {
      const cartResponse = await fetch('/cart.js');
      if (!cartResponse.ok) {
        this.lastEligibilityReason = `could not read /cart.js (HTTP ${cartResponse.status}), so the check failed closed`;
        DeliveryDebug.log('FAIL:', this.lastEligibilityReason);
        return emptyResult;
      }
      const cart = await cartResponse.json();
      const handles = [...new Set((cart.items || []).map((item) => item.handle).filter(Boolean))];

      if (!handles.length) {
        this.lastEligibilityReason = 'the cart is empty, and an empty cart is treated as not eligible';
        DeliveryDebug.log('Cart is empty ->', this.lastEligibilityReason);
        return emptyResult;
      }

      const productChecks = await Promise.all(
        handles.map(async (handle) => {
          try {
            const productResponse = await fetch(`/products/${encodeURIComponent(handle)}.js`);
            if (!productResponse.ok) {
              DeliveryDebug.log(`${handle}: HTTP ${productResponse.status} from /products/${handle}.js, counted as NOT eligible`);
              return { nationwideEligible: false, dhlOnly: false };
            }
            const product = await productResponse.json();

            const tags = Array.isArray(product.tags) ? product.tags : [];
            const normalizedTags = tags.map((tag) => String(tag).toLowerCase());
            const dhlOnly = normalizedTags.includes('dhl only');
            const christmasPreorder = normalizedTags.includes('christmas preorders');
            const nationwideEligible =
              dhlOnly || normalizedTags.includes('nationwide') || christmasPreorder;
            const matchedTag = tags.find((tag) => {
              const normalizedTag = String(tag).toLowerCase();
              return (
                normalizedTag === 'dhl only' ||
                normalizedTag === 'nationwide' ||
                normalizedTag === 'christmas preorders'
              );
            });

            DeliveryDebug.log(
              `${handle}: ${matchedTag ? `nationwide-eligible via tag "${matchedTag}"` : 'NOT nationwide-eligible'}${
                dhlOnly ? ' (DHL only)' : ''
              }${christmasPreorder ? ' (Christmas preorder)' : ''}`,
              `| tags: [${tags.join(', ')}]`
            );

            return { nationwideEligible, dhlOnly };
          } catch (error) {
            DeliveryDebug.log(`${handle}: request failed (${error.message}), counted as NOT eligible`);
            return { nationwideEligible: false, dhlOnly: false };
          }
        })
      );

      const allNationwideEligible = productChecks.every((check) => check.nationwideEligible);
      const hasDhlOnly = productChecks.some((check) => check.dhlOnly);
      const failing = handles.filter((handle, index) => !productChecks[index].nationwideEligible);
      const dhlHandles = handles.filter((handle, index) => productChecks[index].dhlOnly);

      this.lastEligibilityReason = [
        allNationwideEligible
          ? `every cart product is nationwide-eligible via "DHL only", "Nationwide", or "Christmas preorders" (${handles.join(', ')})`
          : `these cart products are not nationwide-eligible: ${failing.join(', ')}`,
        hasDhlOnly
          ? `delivery options section hidden because cart has "DHL only" product(s): ${dhlHandles.join(', ')}`
          : 'delivery options section left visible because cart has no "DHL only" products',
      ].join('; ');

      DeliveryDebug.log(
        `Verdict: all ${handles.length} product(s) nationwide-eligible = ${allNationwideEligible}; has DHL only = ${hasDhlOnly}.`,
        this.lastEligibilityReason
      );

      return { allNationwideEligible, hasDhlOnly };
    } catch (error) {
      this.lastEligibilityReason = `the eligibility check threw (${error.message}), so it failed closed`;
      DeliveryDebug.log('ERROR:', this.lastEligibilityReason);
      return emptyResult;
    }
  }

  // Backwards-compatible wrapper around the shared eligibility check.
  async cartHasOnlyNationwideEligibleProducts() {
    const eligibility = await this.getCartDeliveryEligibility();
    return eligibility.allNationwideEligible;
  }

  applyDeliveryOptionVisibility(eligibility) {
    this.lastDeliveryEligibility = eligibility;
    this.applyingDeliveryVisibility = true;

    try {
      if (eligibility.hasDhlOnly) {
        this.selectNationwideForDhlOnly();
        this.applyDeliverySectionVisibility(true);
        return;
      }

      this.applyDeliverySectionVisibility(false);
      this.applyOptionCellVisibility(
        '#standard-shipping-option-div',
        !eligibility.allNationwideEligible,
        'Nationwide'
      );
      this.applyOptionCellVisibility('#pickup-option-div', false, 'Click & Collect');
    } finally {
      this.applyingDeliveryVisibility = false;
    }
  }

  // PickEasy still needs a fulfilment choice for checkout; select Nationwide while the UI is hidden.
  selectNationwideForDhlOnly() {
    const nationwideOption = document.getElementById('standard-shipping-option-div');
    if (!nationwideOption) {
      DeliveryDebug.log('Could not auto-select Nationwide for DHL-only cart; option not in the DOM yet.');
      return;
    }

    const alreadySelected =
      nationwideOption.classList.contains('ft-options-active') ||
      nationwideOption.classList.contains('btn-selected') ||
      nationwideOption.getAttribute('aria-pressed') === 'true';

    if (alreadySelected) {
      DeliveryDebug.log('Nationwide already selected for DHL-only cart.');
      return;
    }

    DeliveryDebug.log('Auto-selecting Nationwide for DHL-only cart before hiding delivery options.');
    nationwideOption.click();
  }

  applyDeliverySectionVisibility(shouldHide) {
    const selectors = [
      '#ft-options-div',
      '#orders-slots-heading',
      '#orders-slots-div',
      '.order-delivery-locations',
      '.order-slots-error',
      '.pickup-location',
    ];

    selectors.forEach((selector) => {
      document.querySelectorAll(selector).forEach((element) => {
        if (shouldHide) {
          element.style.setProperty('display', 'none', 'important');
          if (element.id === 'ft-options-div') {
            element.setAttribute('data-theme-dhl-only', 'true');
          }
        } else {
          element.style.removeProperty('display');
          if (element.id === 'ft-options-div') {
            element.removeAttribute('data-theme-dhl-only');
          }
        }
      });
    });

    // Restore per-cell displays when showing the section again.
    if (!shouldHide) {
      document.querySelectorAll('#ft-options-div .ft-options-cell').forEach((cell) => {
        cell.style.removeProperty('display');
      });
    }

    DeliveryDebug.log(
      shouldHide
        ? 'Hiding the whole PickEasy delivery options section for DHL-only cart.'
        : 'Showing the PickEasy delivery options section.'
    );
  }

  applyOptionCellVisibility(selector, shouldHide, label) {
    const option = document.querySelector(selector);
    if (!option) {
      DeliveryDebug.log(`${selector} is not in the DOM, so the ${label} option could not be updated.`);
      return;
    }

    const optionCell = option.closest('.ft-options-cell');
    if (!optionCell) {
      DeliveryDebug.log(`Found ${selector} but it has no .ft-options-cell ancestor, so nothing was changed.`);
      return;
    }

    DeliveryDebug.log(`${shouldHide ? 'Hiding' : 'Showing'} the ${label} option.`);

    if (shouldHide) {
      optionCell.style.setProperty('display', 'none', 'important');
    } else {
      optionCell.style.removeProperty('display');
    }
  }

  applyStandardShippingOptionVisibility(shouldHide) {
    this.lastShouldHideStandardShipping = shouldHide;
    this.applyOptionCellVisibility('#standard-shipping-option-div', shouldHide, 'Nationwide');
  }

  // Backwards-compatible alias for any app or script still calling the old name.
  setStandardShippingOptionVisibility(shouldHide) {
    this.applyStandardShippingOptionVisibility(shouldHide);
  }

  setDrawerFooterLoading(isLoading) {
    const drawerFooter = this.querySelector('.drawer__footer');
    if (!drawerFooter) return;

    let loader = drawerFooter.querySelector('.drawer-footer-loader');

    if (isLoading) {
      if (!loader) {
        loader = document.createElement('div');
        loader.className = 'drawer-footer-loader';
        loader.setAttribute('role', 'status');
        loader.setAttribute('aria-live', 'polite');
        loader.innerHTML = `
          <div class="drawer-footer-loader__inner">
            <span class="drawer-footer-loader__spinner" aria-hidden="true"></span>
            <span class="drawer-footer-loader__text">Checking delivery options...</span>
          </div>
        `;
        if (getComputedStyle(drawerFooter).position === 'static') {
          drawerFooter.style.position = 'relative';
        }
        drawerFooter.appendChild(loader);
      }
      return;
    }

    if (loader) loader.remove();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');

    if (this.standardShippingObserver) {
      this.standardShippingObserver.disconnect();
      this.standardShippingObserver = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

}

customElements.define('cart-drawer-items', CartDrawerItems);
